import {
  isCurrentUserAdmin,
  isCurrentUserSuperAdmin,
} from "@risalabs_frontend_org/auth-wrapper";
import { getBearerToken } from "../../../../api/postCall/bearerToken";
import {
  searchForMedicalPaOrdersFromPostgres,
  SearchForMedicalPaOrdersFromPostgresParams,
} from "../../../../api/postgreSQL/postCalls/searchForMedicalPaOrdersFromPostgres";
import { AuthStatusOptionModel } from "../../../../data-model/authStatusOptions";
import { PostgresResponseForMedicalPaOrders } from "../../../../data-model/postgresResponseForMedicalPaOrders";
import { MedicalPaOrdersState } from "../hooks/useMedicalPaOrdersTableData";
import {
  generateMedicalPaOrdersAlgoliaTableDataForAdmin,
  generateMedicalPaOrdersAlgoliaTableDataForNonAdmin,
} from "../utils/medicalPaOrdersTable";

/**
 * Fetches medical PA orders from Postgres using the provided filter/search payload,
 * then transforms the response into table data based on the user's admin role.
 * Resets pagination on completion.
 *
 * @param payload - Search parameters including filters, search query, and pagination
 * @param authStatusOptions - Available auth status options for badge rendering
 * @param setMedicalPaOrdersState - State setter for loading/error/data lifecycle
 * @param setTableData - State setter for the transformed table rows
 * @param setPaginationResetCount - Incrementing setter to trigger pagination reset
 */
export const makePostCallForMedicalPaOrders = async (
  payload: SearchForMedicalPaOrdersFromPostgresParams,
  authStatusOptions: AuthStatusOptionModel[],
  setMedicalPaOrdersState: React.Dispatch<
    React.SetStateAction<MedicalPaOrdersState>
  >,
  setTableData: React.Dispatch<React.SetStateAction<any[]>>,
  setPaginationResetCount: React.Dispatch<React.SetStateAction<number>>,
) => {
  setMedicalPaOrdersState((prev) => ({ ...prev, loading: true }));

  try {
    await getBearerToken();
    const response = await searchForMedicalPaOrdersFromPostgres(payload);
    const data = JSON.parse(response) as PostgresResponseForMedicalPaOrders;

    setMedicalPaOrdersState((prev) => ({ ...prev, data }));

    const tableData =
      isCurrentUserAdmin() || isCurrentUserSuperAdmin()
        ? generateMedicalPaOrdersAlgoliaTableDataForAdmin(
            data.hits ?? [],
            authStatusOptions,
          )
        : generateMedicalPaOrdersAlgoliaTableDataForNonAdmin(
            data.hits ?? [],
            authStatusOptions,
          );

    setTableData(tableData);
  } catch (error) {
    setMedicalPaOrdersState((prev) => ({ ...prev, error: error as string }));
  } finally {
    setMedicalPaOrdersState((prev) => ({ ...prev, loading: false }));
    setPaginationResetCount((prev) => prev + 1);
  }
};
